import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useFundraiser } from "../../pages/ImprovedShopPage/FundraiserContext";
import { Category } from "../../CommonFunctions";
import {
  DesignCategory,
  getDesignCategories,
} from "./constants/designCategories";
import { dynamicCategoryDesignFetcher } from "./DynamicCategoryDesignFetcher";
import { DesignType } from "../../Interfaces/FundraiserRole";
import useResponsive from "../../hooks/useResponsive";
import ProductCategorySelector from "./ProductCategorySelector";
import { sortProductCategories } from "./utils/sortProductCategories";
import { isCustomClothingCategory } from "../../constants/categories";

import "./Shop.css";
import {
  DesignFile,
  ImprovedParticipantShop,
} from "../../Interfaces/FundraiserRole";
import { ImprovedProduct } from "../../Interfaces/Product";
import FilterDesignsButton from "./FilterDesignsButton";
import FloatingCartButton from "./FloatingCartButton";
import DesktopDesignCategoryFilter from "./DesktopDesignCategoryFilter";
import { MobileDesignCategoryFilterDrawer } from "./MobileDesignCategoryFilterDrawer";
import ErrorLoading from "../Common/ErrorLoading";
import LoadingSpinner from "../Common/LoadingSpinner";
import ShopHeader from "./ShopHeader";
import { ITEMS_PER_PAGE } from "./constants";
import DesignOrProductGrid from "./DesignOrProductGrid";
interface ShopProps {
  participantShop: ImprovedParticipantShop;
  isFromSamplePage?: boolean;
}

const errorLoadingComponent = <ErrorLoading />;
const loadingShopComponent = (
  <LoadingSpinner textToDisplay="Loading shop data..." />
);

const Shop: React.FC<ShopProps> = ({
  participantShop,
  isFromSamplePage = false,
}) => {
  const clothingCategoryMap = {
    "Custom Hoodies": "Custom Hoodies",
    "Custom T-Shirts": "Custom T-Shirts",
    "Custom Racerbacks": "Custom Racerbacks",
  } as const;

  const [currentPage, setCurrentPage] = useState(1);
  const [currentDesignPage, setCurrentDesignPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<Category | string>(
    "",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectedClothingCategory, setSelectedClothingCategory] = useState<
    keyof typeof clothingCategoryMap | ""
  >("");
  const [selectedDesignCategories, setSelectedDesignCategories] = useState<
    DesignCategory[]
  >([]);
  const [availableDesigns, setAvailableDesigns] = useState<
    Record<string, string[]>
  >({});
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [mobileDesignFilterOpen, setMobileDesignFilterOpen] = useState(false);

  const { showMobileDesignFilters, showDesktopDesignFilters } = useResponsive();

  const fundraiser = useFundraiser();

  useEffect(() => {
    setLoading(fundraiser.loading);
  }, [fundraiser.loading]);

  const allCategories = useMemo(() => {
    return Object.keys(participantShop.sku_by_category);
  }, [participantShop.sku_by_category]);

  const getFirstInCategory = (design: DesignFile) => {
    const foundProduct = participantShop.products.find(
      (p) => p.sku === participantShop.sku_by_category[selectedCategory][0],
    );

    if (foundProduct) {
      return foundProduct;
    }
    const fallBack: ImprovedProduct = {
      sku: 111,
      name: design?.file_name || "Custom Design",
      description: design?.file_name || "Custom design product",
      base_price: 100,
      images: design?.file_location ? [design.file_location] : [],
      variants: { style_variants: [], color_variants: [] },
      is_customizable: true,
      features: [],
    };
    return fallBack;
  };

  const categories = useMemo(() => {
    return allCategories.filter(
      (category) => !isCustomClothingCategory(category),
    );
  }, [allCategories]);

  const {
    sortedCategories: sortedProductCategories,
    showClothingDropdown,
    clothingCategoryCounts,
  } = useMemo(() => {
    return sortProductCategories(
      allCategories,
      participantShop.sku_by_category,
    );
  }, [allCategories, participantShop.sku_by_category]);

  useEffect(() => {
    if (participantShop?.designs) {
      const designs = Object.values(participantShop.designs).filter(
        Boolean,
      ) as DesignType[];
      const designCategories = dynamicCategoryDesignFetcher(designs);
      setAvailableDesigns(designCategories);

      const categories = getDesignCategories(designs);
      setAvailableCategories(categories);

      setSelectedDesignCategories(categories);
    }
  }, [participantShop?.designs]);

  const handleDesignCategoryToggle = useCallback((category: DesignCategory) => {
    setSelectedDesignCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  }, []);

  const handleClearAllDesignCategories = useCallback(() => {
    setSelectedDesignCategories([]);
  }, []);

  useEffect(() => {
    if (
      sortedProductCategories.length > 0 &&
      !selectedCategory &&
      !selectedClothingCategory
    ) {
      setSelectedCategory(sortedProductCategories[0]);
    }
    if (
      selectedCategory &&
      !sortedProductCategories.includes(selectedCategory) &&
      !(selectedCategory in clothingCategoryMap) &&
      sortedProductCategories.length > 0
    ) {
      setSelectedCategory(sortedProductCategories[0]);
    }
  }, [
    sortedProductCategories,
    selectedCategory,
    selectedClothingCategory,
    clothingCategoryMap,
  ]);

  const handleClothingCategorySelect = (category: string) => {
    if (category in clothingCategoryMap) {
      setSelectedClothingCategory(category as keyof typeof clothingCategoryMap);
      setSelectedCategory("");
    }
  };

  const handleCategorySelect = useCallback((category: string | null) => {
    setSelectedCategory(category || "");
    setSelectedClothingCategory("");
  }, []);

  const getCategorySkus = (category: string) => {
    if (category in clothingCategoryMap) {
      const customCategory =
        clothingCategoryMap[category as keyof typeof clothingCategoryMap];
      return participantShop.sku_by_category[customCategory] || [];
    }
    if (Object.values(clothingCategoryMap).includes(category as any)) {
      return participantShop.sku_by_category[category] || [];
    }
    return participantShop.sku_by_category[category] || [];
  };

  const currentProductSkus = useMemo(() => {
    const skus = selectedClothingCategory
      ? getCategorySkus(selectedClothingCategory)
      : selectedCategory
        ? getCategorySkus(selectedCategory)
        : [];
    return skus;
  }, [
    selectedCategory,
    selectedClothingCategory,
    participantShop.sku_by_category,
  ]);

  const currentProducts: ImprovedProduct[] = useMemo(() => {
    if (!selectedCategory && !selectedClothingCategory) {
      return participantShop.products;
    }

    if (currentProductSkus.length > 0) {
      const filtered = participantShop.products.filter(
        (product: ImprovedProduct) =>
          currentProductSkus.includes(Number(product.sku)),
      );
      return filtered;
    }

    const currentCategory = (
      selectedClothingCategory ||
      selectedCategory ||
      ""
    ).toLowerCase();
    if (currentCategory) {
      const productsInCategory = participantShop.products.filter(
        (product: ImprovedProduct) => {
          const designCategories = product.design_categories || [];
          return designCategories.some((cat: string) =>
            cat.toLowerCase().includes(currentCategory),
          );
        },
      );
      if (productsInCategory.length > 0) {
        return productsInCategory;
      }

      return participantShop.products.filter((product: ImprovedProduct) => {
        const searchStr =
          `${product.name} ${product.description}`.toLowerCase();
        return searchStr.includes(currentCategory);
      });
    }

    return participantShop.products;
  }, [
    currentProductSkus,
    participantShop.products,
    participantShop.designs,
    selectedCategory,
    selectedClothingCategory,
    clothingCategoryMap,
  ]);

  const currentCategory = (
    selectedClothingCategory ||
    selectedCategory ||
    ""
  ).toLowerCase();
  const isDesignCategory = [
    "hoodie",
    "t-shirt",
    "racerback",
    "bear",
    "tumbler",
    "bottle",
    "mug",
  ].some((term) => currentCategory.includes(term));

  const filteredProducts = useMemo(() => {
    if (!isDesignCategory) {
      return currentProducts;
    }

    const filtered = currentProducts.filter((product) => {
      if (selectedDesignCategories.length === 0) return true;
      const designCategories = product.design_categories || [];
      return designCategories.some((category: string) =>
        selectedDesignCategories.includes(category as DesignCategory),
      );
    });

    return filtered;
  }, [currentProducts, selectedDesignCategories, isDesignCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedClothingCategory]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const { designs, designCategories } = useMemo(() => {
    const currentCategory = selectedClothingCategory || selectedCategory || "";
    const categoryName = currentCategory.toLowerCase();

    let designCategories: string[] = [];
    let shouldShowDesigns = false;

    if (
      ["hoodie", "t-shirt", "racerback"].some((term) =>
        categoryName.includes(term),
      )
    ) {
      shouldShowDesigns = true;
      designCategories = ["clothing", "spangle"];
    } else if (categoryName.includes("bear")) {
      shouldShowDesigns = true;
      designCategories = ["bears"];
    } else if (
      ["tumbler", "bottle", "mug"].some((term) => categoryName.includes(term))
    ) {
      shouldShowDesigns = true;
      designCategories = ["bottles"];
    }

    const allDesigns: any[] = [];

    if (shouldShowDesigns && participantShop.designs) {
      designCategories.forEach((category) => {
        const designGroup =
          participantShop.designs?.[
            category as keyof typeof participantShop.designs
          ];
        if (designGroup) {
          Object.values(designGroup).forEach((subcategories: any) => {
            Object.values(subcategories).forEach((designs: any) => {
              allDesigns.push(...designs);
            });
          });
        }
      });
    }

    return { designs: allDesigns, designCategories };
  }, [selectedClothingCategory, selectedCategory, participantShop.designs]);

  const selectRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        selectRef2.current &&
        !selectRef2.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (loading) {
    return loadingShopComponent;
  }

  if (error || !participantShop) {
    return errorLoadingComponent;
  }

  return (
    <section>
      <div className="mx-auto px-3 py-5 py-lg-5 sec-wrapper">
        <ShopHeader />
        <ProductCategorySelector
          categories={allCategories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          participantShop={participantShop}
          clothingCategoryCounts={clothingCategoryCounts}
          sortedProductCategories={sortedProductCategories}
          handleCategorySelect={handleCategorySelect}
          showClothingDropdown={showClothingDropdown}
          selectedClothingCategory={selectedClothingCategory}
          handleClothingCategorySelect={handleClothingCategorySelect}
        />
        <div className="row g-4 w-100 m-0">
          {showDesktopDesignFilters && isDesignCategory && (
            <div className="col-12 col-md-3">
              <div
                className="sticky-top"
                style={{
                  top: "20px",
                  paddingRight: "30px",
                  borderRight: "1px solid #e0e0e0",
                  marginRight: "15px",
                }}
              >
                <DesktopDesignCategoryFilter
                  selectedCategories={selectedDesignCategories}
                  onCategoryToggle={handleDesignCategoryToggle}
                  availableCategories={availableCategories}
                />
              </div>
            </div>
          )}

          {showMobileDesignFilters && isDesignCategory && (
            <>
              <FilterDesignsButton
                setMobileDesignFilterOpen={setMobileDesignFilterOpen}
              />

              <MobileDesignCategoryFilterDrawer
                open={mobileDesignFilterOpen}
                onClose={() => setMobileDesignFilterOpen(false)}
                selectedCategories={selectedDesignCategories}
                onCategoryToggle={handleDesignCategoryToggle}
                onClearAll={handleClearAllDesignCategories}
                onApply={() => {}}
                availableCategories={availableCategories}
              />
            </>
          )}
          <DesignOrProductGrid
            showDesktopDesignFilters={showDesktopDesignFilters}
            designCategories={availableCategories}
            selectedDesignCategories={selectedDesignCategories}
            designs={designs}
            filteredProducts={filteredProducts}
            paginatedProducts={paginatedProducts}
            participantShop={participantShop}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentDesignPage={currentDesignPage}
            setCurrentDesignPage={setCurrentDesignPage}
            selectedCategory={selectedCategory || selectedClothingCategory}
            isFromSamplePage={isFromSamplePage}
          />
        </div>
        <FloatingCartButton />
      </div>
    </section>
  );
};

export default Shop;
